import React, { FunctionComponent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Modal } from 'semantic-ui-react';
import { propOr } from 'ramda';
import withStyles from 'react-jss';
import { isAfter } from 'date-fns';

import { Theme } from 'config';
import { ClassProp } from 'helpers/types';
import Button from 'components/Button';
import { useModal } from 'components/Modal/modalContext';
import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import { UserProfile } from 'components/UserMgmt/UserMgmt.types';
import {
  handleError,
  handleSuccess,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';

import { QueryResponse } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';

import { SET_OFFER_SEGMENT_PROPERTIES } from './OfferSegmentDeleteModal.mutations';
import { GET_OFFER } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/scenes/PulseOffer/PulseOffer.queries';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  offerSegment?: Record<string, any>;
};

const styles = (theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-around',
    gap: '1rem',
  },
  cancelButton: {
    backgroundColor: `${theme.softGray} !important`,
    fontSize: '.875rem',
  },
  button: {
    backgroundColor: `${theme.violet} !important`,
    color: '#fff !important',
    fontSize: '.875rem',
    flex: 2,
  },
});

const OfferSegmentDeleteModal: FunctionComponent<Props> = ({
  classes,
  offerSegment,
}) => {
  const { offerUuid = '' } = useParams<any>();
  const segmentUuid = propOr('', 'uuid', offerSegment);
  const [userName, setUserName] = useState<string | null>(null);
  const startDate: string = propOr('', 'startDate', offerSegment);
  const { handleModal } = useModal();

  const { data, loading: profileLoading } = useQuery<QueryResponse>(
    GET_PROFILE,
  );

  useEffect(() => {
    if (profileLoading) return;
    const userProfile: UserProfile = propOr({}, 'userProfile', data);
    const familyName = propOr('', 'familyName', userProfile);
    const givenName = propOr('', 'givenName', userProfile);
    setUserName(`${givenName} ${familyName}`);
  }, [data, profileLoading]);

  const handleCompleted = () => {
    handleSuccess('Offer segment deleted.');
    handleModal();
  };

  const handleErrors = (error: any) => {
    handleError(error);
    handleModal();
  };

  const [runMutation, { loading: isSubmitting }] = useMutation(
    SET_OFFER_SEGMENT_PROPERTIES,
    {
      onCompleted: handleCompleted,
      onError: handleErrors,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_OFFER,
          variables: {
            params: {
              offerUuid,
              hydrateFinancialTerms: true,
            },
          },
        },
      ],
    },
  );

  if (!isAfter(new Date(startDate), new Date())) {
    return null;
  }

  const handleOnClick = async (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    await runMutation({
      variables: {
        params: {
          segmentUuid,
          segmentEndDate: startDate,
          deleted: true,
          updatedBy: userName,
        },
      },
    });
  };

  const handleCancelAction = () => {
    handleModal();
  };

  return (
    <>
      <Modal.Header>Delete offer segment</Modal.Header>
      <Modal.Content>
        <p>This segment will be deleted. Are you sure?</p>
        <div className={classes.buttons}>
          <Button
            fluid
            disabled={isSubmitting}
            className={classes.cancelButton}
            onClick={handleCancelAction}
          >
            No
          </Button>
          <Button
            disabled={isSubmitting}
            loading={isSubmitting}
            className={classes.button}
            onClick={handleOnClick}
          >
            Yes
          </Button>
        </div>
      </Modal.Content>
    </>
  );
};

export default withStyles(styles)(OfferSegmentDeleteModal);
